import type { JSX } from 'react';
import { useLocale } from '../i18n/index';
import { duration } from '../lib/format';
import {
  type DashboardRow,
  latenessBandOf,
  latenessDetailMinutes,
  latenessIsBadNews,
  severityOf,
} from '../lib/lateness';
import { StatusPill } from './StatusPill';

/**
 * The margin between the promised window and the courier, as a sentence, or
 * `null` when there is no honest figure to state (PRD OPS-001, RTE-006).
 *
 * The null case is the one that matters and it is not decoration. A broken or
 * settled status has no honest minutes figure - arithmetic on a dead job's ETA
 * contradicts everything else the screen says. "Late - 17 min spare" was the
 * exact defect, on a real row.
 *
 * One derivation, because the board and the order's own record both state this
 * margin and used to state it in two places - the board as a component, the
 * record as an inline ternary and an IIFE with the same three branches copied
 * into it. Both already called the same two derivations (`severityOf`,
 * `latenessDetailMinutes`), so neither could print a different NUMBER; what
 * could drift, and is what a copy of a sentence always drifts on, is the
 * wording and the null case.
 */
function useRiskSentence(row: DashboardRow, now: Date | null = null): string | null {
  const { t } = useLocale();
  const minutes = latenessDetailMinutes(row, now);
  if (minutes === null) return null;
  // Days, hours and minutes, not "27660 min" (founder, 17 October 2026).
  if (minutes > 0) return t('severity.lateBy', { delta: duration(minutes * 60) });
  if (minutes < 0) return t('severity.slack', { delta: duration(minutes * 60) });
  return t('severity.onTime');
}

/**
 * How an order stands against its promised window on the BOARD: the band as a
 * badge, and the minutes that separate two rows inside one band.
 *
 * The badge belongs to the board and to nowhere else. Forty rows are scanned
 * rather than read, so the band has to be a colour before it is a word; a
 * record is READ, and states its one state as the pill at the top right of its
 * first section instead (founder, 2026-09-06).
 */
export function RiskCell({ row }: { row: DashboardRow }): JSX.Element {
  const sentence = useRiskSentence(row);
  // A stopped order draws nothing here: its status already says so (P32).
  const band = latenessBandOf(severityOf(row));

  return (
    <div className="internal-risk">
      {band === null ? null : <StatusPill family="latenessBand" state={band} />}
      {sentence === null ? null : <span className="internal-muted">{sentence}</span>}
    </div>
  );
}

/**
 * The same margin on the order RECORD: one plain sentence, red when it is bad
 * news, and no badge (founder, 2026-09-06 - "keep it as a plain sentence, put
 * the text in red when it is bad info").
 *
 * The band's own badge used to sit above this sentence, which is what made the
 * record carry three pills saying one thing between them: an order the state
 * pill called "On the way" was labelled "On track" here and "Picked up" on the
 * delivery fact beside it, and a DELIVERED order was still labelled "On track",
 * which is the one thing it had stopped being. The record has ONE pill now and
 * it is the state machine's, so this sentence carries the whole of what it
 * says - hence the colour, which is asked of `latenessIsBadNews` rather than of
 * a threshold of its own, so the record and the board can never disagree about
 * which orders are in trouble.
 *
 * An em dash where there is no honest figure, as everywhere else on this
 * screen. It costs nothing: a row with no margin to state - stopped, broken or
 * arrived - is a row whose one pill already says so in words.
 */
export function RiskSentence({
  row,
  now = null,
}: {
  row: DashboardRow;
  /** The platform clock: an ETA already behind it is measured to now. */
  now?: Date | null;
}): JSX.Element {
  const sentence = useRiskSentence(row, now);
  if (sentence === null) return <span className="internal-muted">&mdash;</span>;
  return <span className={latenessIsBadNews(row) ? 'internal-bad' : undefined}>{sentence}</span>;
}
